import * as Sentry from '@sentry/remix';
import { CardElement } from '@stripe/react-stripe-js';
import type { Stripe } from '@stripe/stripe-js';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';

interface Props {
  stripe: Stripe | null;
  elements: any;
  isSuccessful: boolean;
  isFailed: boolean;
  data: any;
  hasRedirected: boolean;
  applePayEvent: any;
  setApplePayEvent: (event: any) => void;
  setCookie: (options: any) => void;
  setHasRedirected: (hasRedirected: boolean) => void;
  onContinue?: () => void;
  handleRemoveDisabledState: () => void;
  isGift?: boolean;
}

const useHandleCheckoutSubmissionStates = ({
  stripe,
  elements,
  isSuccessful,
  isFailed,
  data,
  hasRedirected,
  applePayEvent,
  setApplePayEvent,
  setCookie,
  setHasRedirected,
  onContinue,
  handleRemoveDisabledState,
  isGift = false,
}: Props) => {
  const [isProcessing3DSecure, setIsProcessing3DSecure] = useState(false);

  const removeApplePayEvent = (status: 'success' | 'fail') => {
    if (applePayEvent) {
      applePayEvent.complete(status);
      setApplePayEvent(null);
    }
  };

  const handleSuccess = () => {
    removeApplePayEvent('success');
    if (isGift) {
      toast.success(t('upgrade.tabs.gift.success.message'));
    } else {
      toast.success(t('upgrade.success.message'));
    }
    onContinue?.();
    setHasRedirected(true);
    if (!isGift) {
      setCookie({ name: 'active_upgrade', value: 'true', days: 4 });
    }
    setIsProcessing3DSecure(false);
    handleRemoveDisabledState();
  };

  const handleFail = () => {
    removeApplePayEvent('fail');
    const errorMessage = data?.toast?.message || '';
    toast.error(errorMessage || t('errors.default'));
    if (!isGift) {
      setCookie({ name: 'active_upgrade', value: 'true', days: 0 });
    }
    setIsProcessing3DSecure(false);
    handleRemoveDisabledState();
  };

  const clientSecret = data?.clientSecret;
  const action = data?.action;
  const handleConfirmPayment = async (clientSecret: string) => {
    setIsProcessing3DSecure(true);

    try {
      if (action === 'requires_confirmation') {
        const cardElement = elements.getElement(CardElement);
        await stripe?.confirmCardSetup(clientSecret, {
          payment_method: { card: cardElement },
        });
      } else if (action === 'requires_action') {
        const cardElement = elements.getElement(CardElement);
        await stripe?.confirmCardPayment(clientSecret, {
          payment_method: { card: cardElement },
        });
      } else if (applePayEvent) {
        await stripe?.confirmCardPayment(clientSecret, {
          payment_method: applePayEvent.paymentMethod.id,
        });
      }

      handleSuccess();
    } catch (error) {
      Sentry.captureException(error);
      handleFail();
    }
  };

  // On Attempts that where additional authentication or actions by the user are required
  useEffect(() => {
    if (clientSecret && action) {
      handleConfirmPayment(clientSecret);
    }
  }, [clientSecret, action]);

  // On successful or failed attemps
  useEffect(() => {
    if (isSuccessful && !hasRedirected) {
      handleSuccess();
    } else if (isFailed) {
      handleFail();
    }
  }, [isSuccessful, isFailed, hasRedirected]);

  return { isProcessing3DSecure };
};

export default useHandleCheckoutSubmissionStates;
